import {
  CONSENT_STATE_SCHEMA_VERSION,
  OPTIONAL_CONSENT_CATEGORIES,
} from './constants.js';
import { ConsentStateTransitionError } from './errors.js';
import {
  hasCanonicalConsentChoices,
  isCanonicalConsentId,
  isCanonicalPolicyVersion,
  isCanonicalUtcTimestamp,
} from './validation.js';
import type {
  ConsentChoices,
  ConsentDecision,
  ConsentPreferenceInput,
  ConsentState,
  ConsentTransitionContext,
} from './types.js';

function validateContext(context: ConsentTransitionContext): void {
  if (!isCanonicalConsentId(context.consentId)) {
    throw new ConsentStateTransitionError(
      'invalid_consent_id',
      'Consent ID must use the gcs_ prefix followed by a canonical ULID.',
    );
  }

  if (
    !Number.isSafeInteger(context.configurationVersion) ||
    context.configurationVersion < 1
  ) {
    throw new ConsentStateTransitionError(
      'invalid_configuration_version',
      'Configuration version must be a positive safe integer.',
    );
  }

  if (!isCanonicalPolicyVersion(context.policyVersion)) {
    throw new ConsentStateTransitionError(
      'invalid_policy_version',
      'Policy version must use the supported canonical format.',
    );
  }

  if (!isCanonicalUtcTimestamp(context.updatedAt)) {
    throw new ConsentStateTransitionError(
      'invalid_updated_at',
      'Updated timestamp must be a valid UTC ISO date string.',
    );
  }
}

function validatePreviousState(
  previous: ConsentState,
  context: ConsentTransitionContext,
): void {
  if (previous.id !== context.consentId) {
    throw new ConsentStateTransitionError(
      'consent_id_mismatch',
      'Previous state and transition context must use the same consent ID.',
    );
  }

  if (previous.schemaVersion !== CONSENT_STATE_SCHEMA_VERSION) {
    throw new ConsentStateTransitionError(
      'unsupported_schema_version',
      'Previous state uses an unsupported schema version.',
    );
  }

  if (
    !Number.isSafeInteger(previous.revision) ||
    previous.revision < 1 ||
    !hasCanonicalConsentChoices(previous.categories)
  ) {
    throw new ConsentStateTransitionError(
      'invalid_previous_state',
      'Previous state is malformed.',
    );
  }
}

function validatePreferences(preferences: ConsentPreferenceInput): void {
  for (const [key, value] of Object.entries(preferences)) {
    if (
      !OPTIONAL_CONSENT_CATEGORIES.includes(
        key as (typeof OPTIONAL_CONSENT_CATEGORIES)[number],
      ) ||
      typeof value !== 'boolean'
    ) {
      throw new ConsentStateTransitionError(
        'invalid_preferences',
        'Preferences may contain only boolean optional consent categories.',
      );
    }
  }
}

function choicesForDecision(
  previous: ConsentState | null,
  decision: ConsentDecision,
): ConsentChoices {
  if (decision.type === 'accept_all') {
    return Object.freeze({
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
    });
  }

  if (decision.type === 'reject_all') {
    return Object.freeze({
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
    });
  }

  validatePreferences(decision.preferences);

  return Object.freeze({
    necessary: true,
    preferences:
      decision.preferences.preferences ??
      previous?.categories.preferences ??
      false,
    analytics:
      decision.preferences.analytics ?? previous?.categories.analytics ?? false,
    marketing:
      decision.preferences.marketing ?? previous?.categories.marketing ?? false,
  });
}

export function applyConsentDecision(
  previous: ConsentState | null,
  decision: ConsentDecision,
  context: ConsentTransitionContext,
): ConsentState {
  validateContext(context);

  if (previous) {
    validatePreviousState(previous, context);
  }

  const nextRevision = (previous?.revision ?? 0) + 1;

  if (!Number.isSafeInteger(nextRevision)) {
    throw new ConsentStateTransitionError(
      'invalid_previous_state',
      'Consent revision exceeds the supported safe integer range.',
    );
  }

  return Object.freeze({
    id: context.consentId,
    schemaVersion: CONSENT_STATE_SCHEMA_VERSION,
    revision: nextRevision,
    policyVersion: context.policyVersion,
    configurationVersion: context.configurationVersion,
    categories: choicesForDecision(previous, decision),
    updatedAt: context.updatedAt,
  });
}

export function acceptAll(
  previous: ConsentState | null,
  context: ConsentTransitionContext,
): ConsentState {
  return applyConsentDecision(previous, { type: 'accept_all' }, context);
}

export function rejectAll(
  previous: ConsentState | null,
  context: ConsentTransitionContext,
): ConsentState {
  return applyConsentDecision(previous, { type: 'reject_all' }, context);
}

export function savePreferences(
  previous: ConsentState | null,
  preferences: ConsentPreferenceInput,
  context: ConsentTransitionContext,
): ConsentState {
  return applyConsentDecision(
    previous,
    { type: 'preferences_saved', preferences },
    context,
  );
}
